import React from 'react';
import EmptyState from '../shared/EmptyState';
import { filedMeasureBaseline, baselineMissingCopy } from '../../../logic/baseline';
import { oapTrackOf } from '../../../logic/oapAttainment';

// The four program labs. Keys match WF72's import vocabulary and the patient plan card's, which is
// what stops the care team seeing a different set of labs than the patient does.
const LAB_MEASURES = [
  { key: 'hba1c', label: 'HbA1c', unit: '%' },
  { key: 'ldl_c', label: 'LDL cholesterol', unit: 'mg/dL' },
  { key: 'egfr', label: 'eGFR', unit: 'mL/min/1.73m²' },
  { key: 'uacr', label: 'uACR', unit: 'mg/g' },
];

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// A value is only as trustworthy as its date. `latest_at` is what the lab reports as the COLLECTION
// date (WF72 writes it from the chart, WF37's manual entry from the operator's typed date), so a
// value with no date is shown without one rather than with today's.
const collectedOn = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : null;
};

/**
 * A `= null` default in a .jsx file makes tsc infer `null | undefined`, so callers passing a real
 * value fail to type-check — annotate instead (same trap as CompleteNotifyModal/TaskCard).
 * @param {{ measureStates?: any[], cmsSubmissions?: any[] | null, withingsUserId?: any, trackCode?: string | null, subtrackId?: string | null }} props
 */
export default function LabResultsTab({
  measureStates = [], cmsSubmissions = null, withingsUserId = null, trackCode = null, subtrackId = null,
}) {
  // Unknown track ⇒ null ⇒ no track claim, the three filing facts stand.
  const track = oapTrackOf(trackCode, subtrackId);
  const byType = {};
  for (const ms of (measureStates || [])) {
    if (ms && ms.measure_type) byType[String(ms.measure_type)] = ms;
  }
  // A row earns its place if EITHER number exists: dropping a filed baseline alongside a missing
  // current value would claim "no lab results" while CMS holds filed baselines.
  const rows = LAB_MEASURES
    .map((m) => {
      const ms = byType[m.key] || null;
      return {
        ...m,
        ms,
        value: ms ? num(ms.latest_value) : null,
        filed: filedMeasureBaseline(m.key, cmsSubmissions, withingsUserId, { measureStates, track }),
      };
    })
    .filter((r) => r.value !== null || r.filed != null);

  if (rows.length === 0) {
    return <EmptyState title="No lab results available yet." />;
  }

  return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '16px 20px' }}>
      {rows.map((r) => {
        // A date with no value beside it says nothing, so it is only rendered for a real result.
        const when = collectedOn(r.ms?.latest_at);
        let dateCopy = null;
        if (r.value !== null) dateCopy = when ? `collected ${when}` : 'collection date not recorded';
        return (
          <div key={r.key} style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 10 }}>
            <span className="body2" style={{ fontWeight: 500, minWidth: 150 }}>{r.label}</span>
            {r.value !== null && (
              <span className="body2">
                {r.value}
                <span className="text-secondary" style={{ marginLeft: 4 }}>{r.unit}</span>
              </span>
            )}
            {r.value === null && <span className="body2 text-secondary">no current result</span>}
            {dateCopy && <span className="detail1 text-secondary">{dateCopy}</span>}
            {/* Only what logic/baseline.ts proves CMS received may carry the word "Baseline". */}
            <span className="detail1 text-secondary" style={{ marginLeft: 'auto' }}>
              Baseline: {r.filed ?? baselineMissingCopy(cmsSubmissions, withingsUserId, { measureType: r.key, track })}
            </span>
          </div>
        );
      })}
    </div>
  );
}
